import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    Box,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import FavoriteButton from '../FavoriteButton';
import {ActualVsScheduled} from '@/components/Capacity/ActualVsScheduled';
import AddEmployeeCapacityOverrideModal from '@/components/Capacity/AddEmployeeCapacityOverrideModal';
import CapacityHours from '@/components/Capacity/CapacityHours';
import EmployeeTypeIcon from '@/components/Capacity/EmployeeTypeIcon';
import ContainerCenteredGrid from '@/components/ContainerCenteredGrid';
import shortMonths from '@/constants/shortMonths';
import buildCapacityCells from '@/functions/buildCapacityCells';
import buildCapacityDefaultTotals from '@/functions/buildCapacityDefaultTotals';
import buildCapacityOverrideTotals from '@/functions/buildCapacityOverrideTotals';
import buildConfigPerFiscalMonth from '@/functions/buildConfigPerFiscalMonth';
import {useDeleteCapacityFutureTime} from '@/hooks/capacity/useDeleteCapacityFutureTime';
import type {EmployeeActuals} from '@/types/Actuals';
import type {CapacityCombined} from '@/types/CapacityCombined';
import type {CapacityConfig} from '@/types/CapacityConfig';
import type {CapacityDefault} from '@/types/CapacityDefault';
import type {CapacityLabel} from '@/types/CapacityLabel';
import type {ClientServiceTeam} from '@/types/ClientServiceTeam';
import type {Employee} from '@/types/Employee';

type Props = {
    employee : Employee;
    teams : ClientServiceTeam[];
    fiscalMonth : string;
    labels : CapacityLabel[];
    capacityCombined : CapacityCombined[];
    defaultCapacityYearMonth : string;
    capacityConfig : CapacityConfig;
    actual ?: EmployeeActuals;
    capacityDefaults ?: CapacityDefault[];
    favorite ?: {
        isFavorite : boolean;
        onFavoriteChange : (employeeId : number, isFavorite : boolean) => Promise<void>;
    };
};

const EmployeeCapacityTable = (
    {
        employee,
        teams,
        fiscalMonth,
        labels,
        capacityCombined,
        defaultCapacityYearMonth,
        capacityConfig,
        favorite,
        actual,
        capacityDefaults,
    } : Props
) : React.ReactElement => {
    const deleteCapacityFutureTimeQuery = useDeleteCapacityFutureTime();
    const [open, setOpen] = useState(false);
    const [internalTimeCommitments, setInternalTimeCommitments] = useState<Record<string, number> | null>(null);
    const [timeOffCommitments, setTimeOffCommitments] = useState<Record<string, number> | null>(null);
    const team = teams.find(team => team.id === employee.teamId);
    const capacityYearMonthSplit = fiscalMonth.split('-');
    const yearMonthsDisplay = buildCapacityCells(
        parseInt(capacityYearMonthSplit[0]),
        parseInt(capacityYearMonthSplit[1])
    );
    const configPerFiscalMonth = buildConfigPerFiscalMonth(capacityConfig, yearMonthsDisplay);

    const uniqueLabelIds = [...new Set(capacityCombined.map(label => label.timeLabelID))];
    const uniqueLabels = labels
        .filter(label => uniqueLabelIds.includes(label.timeLabelID) && !label.locked);

    const lockedLabels = labels.filter(label => label.locked);

    const handleDeleteFutureTime = (labelId : number) => {
        deleteCapacityFutureTimeQuery.mutate({
            associateId: employee.id,
            labelId: labelId,
        });
    };

    useEffect(() => {
        setInternalTimeCommitments(buildCapacityOverrideTotals(
            yearMonthsDisplay,
            uniqueLabels,
            capacityCombined
        ));

        setTimeOffCommitments(buildCapacityDefaultTotals(
            yearMonthsDisplay,
            lockedLabels,
            capacityCombined
        ));
    }, [capacityCombined]);

    if (internalTimeCommitments === null || timeOffCommitments === null) {
        return <ContainerCenteredGrid>
            <CircularProgress/>
        </ContainerCenteredGrid>;
    }

    return <TableContainer component={Paper} sx={{mb: 2}}>
        <Table size="small">
            <TableHead>
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(173, 201, 213)',
                    }}
                >
                    <TableCell
                        sx={{
                            width: '25% !important',
                        }}
                    >
                        <Stack
                            sx={{
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            {favorite && (
                                <FavoriteButton
                                    employeeId={employee.id}
                                    isFavorite={favorite.isFavorite}
                                    onFavoriteChange={favorite.onFavoriteChange}
                                />
                            )}
                            <EmployeeTypeIcon employee={employee}/>
                            <div>
                                {team?.abbreviation} {employee.firstName} {employee.lastName}
                            </div>
                            {/*<RefreshButton employee={employee} startDate={startDate}/>*/}
                            <AddEmployeeCapacityOverrideModal
                                employee={employee}
                                open={open}
                                setOpen={setOpen}
                                capacityLabels={labels}
                                defaultCapacityYearMonth={defaultCapacityYearMonth}
                            />
                        </Stack>
                    </TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        const foundMonth = configPerFiscalMonth[capacityYearMonth];
                        const subDisplay = foundMonth.subDisplay;
                        const split = capacityYearMonth.split('-');
                        const month = parseInt(split[1]) - 1;

                        return <TableCell
                            key={`header-cell-${capacityYearMonth}`}
                            align='center'
                            sx={{
                                pr: 3,
                            }}
                        >
                            <Stack
                                sx={{
                                    flexDirection: 'column',
                                    width: '100%',
                                    alignItems: 'center',
                                    pr: '1px',
                                }}
                            >
                                <Box sx={{fontWeight: 800}}>{shortMonths[month]} {split[0]}</Box>
                                {subDisplay && <Box>{subDisplay}</Box>}
                            </Stack>
                        </TableCell>;
                    })}
                </TableRow>
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(173, 201, 213)',
                    }}
                >
                    <TableCell
                        sx={{
                            width: '25% !important',
                        }}
                    >
                        Hours in the Fiscal Month
                    </TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        const foundMonth = configPerFiscalMonth[capacityYearMonth];

                        return <TableCell
                            key={`header-cell-${capacityYearMonth}`}
                            align='center'
                            sx={{
                                pr: 3,
                            }}
                        >
                            {foundMonth.fiscal * 8}
                        </TableCell>;
                    })}
                </TableRow>
            </TableHead>
            <TableBody>
                {uniqueLabels
                    .sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    })
                    .map(label => {
                        const foundCombined = capacityCombined
                            .find(combined => combined.timeLabelID === label.timeLabelID);

                        return <TableRow key={`capacity-label-row-${label.timeLabelID}`}>
                            <TableCell>
                                <Stack direction='row' justifyContent='space-between'>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {label.name}
                                    </Box>
                                    <IconButton
                                        aria-label="delete"
                                        onClick={() => {
                                            handleDeleteFutureTime(label.timeLabelID);
                                        }}
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <DeleteOutlineIcon/>
                                    </IconButton>
                                </Stack>
                            </TableCell>
                            {yearMonthsDisplay.map((capacityYearMonth, index) => {
                                return <CapacityHours
                                    key={`capacity-time-row-${label.timeLabelID}-${capacityYearMonth}`}
                                    labelId={label.timeLabelID}
                                    capacityYearMonth={capacityYearMonth}
                                    combinedHours={foundCombined?.hours[index] ?? 0}
                                    capacityDefaults={capacityDefaults}
                                    actual={actual}
                                    employee={employee}
                                />;
                            })}
                        </TableRow>;
                    })}
                {uniqueLabels.length > 0 && <TableRow
                    key='capacity-label-row-internal-totals'
                    sx={{
                        backgroundColor: 'rgb(224, 237, 244)',
                    }}
                >
                    <TableCell>Internal Sub-Total</TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        return <TableCell
                            key={`capacity-time-row-${capacityYearMonth}`}
                            align='center'
                            sx={{
                                fontWeight: 800,
                                textAlign: 'flex-end',
                            }}
                        >
                            <ActualVsScheduled
                                scheduled={internalTimeCommitments[capacityYearMonth]}
                                actual={actual}
                                capacityYearMonth={capacityYearMonth}
                            />
                        </TableCell>;
                    })}
                </TableRow>}
                {lockedLabels
                    .sort((a, b) => {
                        return a.name.localeCompare(b.name);
                    })
                    .map(label => {
                        const foundCombined = capacityCombined
                            .find(combined => combined.timeLabelID === label.timeLabelID);

                        return <TableRow
                            key={`capacity-label-row-${label.timeLabelID}-${employee.id}`}
                        >
                            <TableCell>{label.name}</TableCell>
                            {yearMonthsDisplay.map((capacityYearMonth, index) => {
                                return <CapacityHours
                                    key={`capacity-time-row-${label.timeLabelID}-${capacityYearMonth}`}
                                    labelId={label.timeLabelID}
                                    capacityYearMonth={capacityYearMonth}
                                    actual={actual}
                                    combinedHours={foundCombined?.hours[index] ?? 0}
                                    employee={employee}
                                />;
                            })}
                        </TableRow>;
                    })}
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(224, 237, 244)',
                    }}
                >
                    <TableCell>Time Out Subtotal</TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        return <TableCell
                            key={`capacity-time-out-totals-${capacityYearMonth}-${employee.id}`}
                            align='center'
                            sx={{
                                fontWeight: 800,
                                textAlign: 'flex-end',
                            }}
                        >
                            <ActualVsScheduled
                                scheduled={timeOffCommitments[capacityYearMonth]}
                                actual={actual}
                                capacityYearMonth={capacityYearMonth}/>
                        </TableCell>;
                    })}
                </TableRow>
                <TableRow
                    sx={{
                        backgroundColor: 'rgb(224, 237, 244)',
                    }}
                >
                    <TableCell>Monthly Capacity</TableCell>
                    {yearMonthsDisplay.map(capacityYearMonth => {
                        const fiscalDays = configPerFiscalMonth[capacityYearMonth].fiscal;
                        const internalTime = internalTimeCommitments[capacityYearMonth];
                        const timeOff = timeOffCommitments[capacityYearMonth];
                        const capacityPerDay = ((fiscalDays * 8) - internalTime) / fiscalDays;
                        const capacityPerMonth = (capacityPerDay * fiscalDays) - (timeOff / 8 * capacityPerDay);

                        return <TableCell
                            key={`capacity-total-totals-${capacityYearMonth}-${employee.id}`}
                            align='center'
                            sx={{
                                fontWeight: 800,
                                textAlign: 'flex-end',
                            }}
                        >
                            <ActualVsScheduled
                                scheduled={isNaN(capacityPerMonth) ? 0 : Math.round(capacityPerMonth * 10) / 10}
                                actual={actual}
                                capacityYearMonth={capacityYearMonth}
                                client={true}/>
                        </TableCell>;
                    })}
                </TableRow>
            </TableBody>
        </Table>
    </TableContainer>;
};

export default EmployeeCapacityTable;
